'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

// Rows of a standard ANSI layout by KeyboardEvent.code, which identifies the
// PHYSICAL key regardless of the user's layout language. That is exactly what
// a hardware tester wants: the E key position lights up whether it types e or у.
const ROWS: string[][] = [
  ['Escape', 'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'],
  [
    'Backquote',
    'Digit1',
    'Digit2',
    'Digit3',
    'Digit4',
    'Digit5',
    'Digit6',
    'Digit7',
    'Digit8',
    'Digit9',
    'Digit0',
    'Minus',
    'Equal',
    'Backspace',
  ],
  [
    'Tab',
    'KeyQ',
    'KeyW',
    'KeyE',
    'KeyR',
    'KeyT',
    'KeyY',
    'KeyU',
    'KeyI',
    'KeyO',
    'KeyP',
    'BracketLeft',
    'BracketRight',
    'Backslash',
  ],
  [
    'CapsLock',
    'KeyA',
    'KeyS',
    'KeyD',
    'KeyF',
    'KeyG',
    'KeyH',
    'KeyJ',
    'KeyK',
    'KeyL',
    'Semicolon',
    'Quote',
    'Enter',
  ],
  [
    'ShiftLeft',
    'KeyZ',
    'KeyX',
    'KeyC',
    'KeyV',
    'KeyB',
    'KeyN',
    'KeyM',
    'Comma',
    'Period',
    'Slash',
    'ShiftRight',
  ],
  ['ControlLeft', 'MetaLeft', 'AltLeft', 'Space', 'AltRight', 'MetaRight', 'ControlRight'],
];

function shortLabel(code: string): string {
  return code
    .replace(/^Key|^Digit/, '')
    .replace('Backquote', '`')
    .replace('BracketLeft', '[')
    .replace('BracketRight', ']')
    .replace('Backslash', '\\')
    .replace('Semicolon', ';')
    .replace('Quote', "'")
    .replace('Comma', ',')
    .replace('Period', '.')
    .replace('Slash', '/')
    .replace('Minus', '-')
    .replace('Equal', '=')
    .replace(/Left$/, ' L')
    .replace(/Right$/, ' R');
}

export default function BrowserKeyboardTester() {
  const [pressed, setPressed] = useState<Set<string>>(new Set());
  const [tested, setTested] = useState<Set<string>>(new Set());
  const [lastEvent, setLastEvent] = useState<{ code: string; key: string } | null>(null);
  const usageReported = useRef(false);

  useEffect(() => {
    function down(event: KeyboardEvent) {
      // The tester exists to observe keys, so browser shortcuts stay suppressed
      // while the page is focused (F5, space scrolling, and friends).
      event.preventDefault();
      setPressed((current) => new Set(current).add(event.code));
      setTested((current) => new Set(current).add(event.code));
      setLastEvent({ code: event.code, key: event.key });
      if (!usageReported.current) {
        usageReported.current = true;
        void consumeToolUsage('keyboard-tester', '').catch(() => undefined);
      }
    }
    function up(event: KeyboardEvent) {
      setPressed((current) => {
        const next = new Set(current);
        next.delete(event.code);
        return next;
      });
    }
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Test your keyboard</h2>
          <p>
            Press any key: it lights up while held and stays marked once tested. A key that never
            lights up is not reaching the computer. Everything runs locally.
          </p>
        </div>
        <span className="tool-live-badge">Keys</span>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          overflowX: 'auto',
          padding: '0.5rem 0',
        }}
      >
        {ROWS.map((row, rowIndex) => (
          <div key={rowIndex} style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
            {row.map((code) => {
              const isDown = pressed.has(code);
              const wasTested = tested.has(code);
              const wide =
                code === 'Space' ? 220 : /Backspace|CapsLock|Enter|Shift|Tab/.test(code) ? 76 : 44;
              return (
                <span
                  key={code}
                  style={{
                    alignItems: 'center',
                    background: isDown
                      ? '#2a78d6'
                      : wasTested
                        ? 'rgba(27, 175, 122, 0.35)'
                        : 'var(--surface-2, #1c2230)',
                    border: '1px solid var(--border, #3a4152)',
                    borderRadius: 6,
                    color: '#fff',
                    display: 'flex',
                    fontSize: 11,
                    height: 38,
                    justifyContent: 'center',
                    minWidth: wide,
                    transition: 'background 80ms',
                  }}
                >
                  {shortLabel(code)}
                </span>
              );
            })}
          </div>
        ))}
      </div>

      <div className="tool-live-stats" style={{ marginTop: '0.75rem' }}>
        <div>
          <strong>{tested.size}</strong>
          <span>keys tested this session</span>
        </div>
        {lastEvent ? (
          <div>
            <strong>{lastEvent.code}</strong>
            <span>last key, typed “{lastEvent.key === ' ' ? 'space' : lastEvent.key}”</span>
          </div>
        ) : null}
      </div>

      <p className="tool-live-panel-note">
        Green means the key registered at least once; blue means held right now. A key stuck blue
        without your finger on it is a stuck switch. Some laptop function keys are captured by the
        system before the browser ever sees them, which is the OS, not the keyboard.
      </p>
    </article>
  );
}
